import { weiToSatoshi } from "boltz-swaps/evm";
import { SwapType } from "boltz-swaps/types";
import {
    type Accessor,
    Show,
    createEffect,
    createMemo,
    createResource,
    onMount,
} from "solid-js";

import { config } from "../config";
import { LBTC } from "../consts/Assets";
import { useCreateContext } from "../context/Create";
import { useGlobalContext } from "../context/Global";
import { useWeb3Signer } from "../context/Web3";
import { isConfidentialAddress } from "../utils/compat";
import { GasAbstractionType } from "../utils/swapCreator";
import { getClaimAddress } from "./CreateButton";
import FeesCollapse from "./FeesCollapse";
import Denomination from "./settings/Denomination";

const ppmFactor = 10_000;

// When sending to an unconfidential address, we need to add an extra
// confidential OP_RETURN output with 1 sat inside
export const unconfidentialExtra = 5;

const gasAbstractionExtraGasCost = 157_000n;

export const getFeeHighlightClass = (
    fee: number,
    regularFee?: number,
): string | undefined => {
    if (regularFee === undefined) {
        return undefined;
    }

    if (fee < 0) {
        return "negative-fee";
    }

    if (fee >= 0 && fee < regularFee) {
        return "lower-fee";
    }

    return "";
};

export const isToUnconfidentialLiquid = ({
    assetReceive,
    addressValid,
    onchainAddress,
}: {
    assetReceive: Accessor<string>;
    addressValid: Accessor<boolean>;
    onchainAddress: Accessor<string>;
}) =>
    assetReceive() === LBTC &&
    addressValid() &&
    !isConfidentialAddress(onchainAddress());

export const RoutingFee = () => {
    const { t } = useGlobalContext();
    const { pair } = useCreateContext();

    return (
        <Show when={pair().maxRoutingFee !== undefined}>
            <span class="fees-extra-line">
                {t("routing_fee_limit")}:{" "}
                <span data-testid="routing-fee-limit">
                    {pair().maxRoutingFee! * ppmFactor} ppm
                </span>
            </span>
        </Show>
    );
};

const Fees = () => {
    const { t, pairs, fetchPairs, notify, fetchRegularPairs } =
        useGlobalContext();
    const {
        pair,
        setMaximum,
        setMinimum,
        setLimitsLoading,
        setMinerFee,
        setBoltzFee,
        onchainAddress,
        addressValid,
        getGasToken,
    } = useCreateContext();
    const { signer, getGasAbstractionSigner } = useWeb3Signer();

    const assetSend = () => pair().fromAsset;
    const assetReceive = () => pair().toAsset;

    const gasAbstractionTrigger = createMemo(() => {
        return {
            signer: signer(),
            assetReceive: assetReceive(),
            assetSend: assetSend(),
        };
    });
    const [gasAbstractionExtraCost] = createResource(
        gasAbstractionTrigger,
        async ({ signer, assetReceive, assetSend }) => {
            if (signer === undefined) {
                return 0;
            }

            const { gasAbstraction, gasPrice } = await getClaimAddress(
                () => assetReceive,
                () => assetSend,
                () => signer,
                onchainAddress,
                getGasAbstractionSigner,
                getGasToken(),
            );
            switch (gasAbstraction.claim) {
                case GasAbstractionType.RifRelay:
                    notify("success", t("rif_extra_fee"));
                    return Number(
                        weiToSatoshi(gasPrice * gasAbstractionExtraGasCost),
                    );

                case GasAbstractionType.None:
                case GasAbstractionType.Signer:
                    return 0;
            }
        },
        { initialValue: 0 },
    );

    createEffect(() => {
        // Updating the miner fee with "setMinerFee(minerFee() + gasAbstractionExtraCost())"
        // causes an endless loop of triggering the effect again
        const updateMinerFee = (fee: number) => {
            setMinerFee(fee + gasAbstractionExtraCost());
        };

        if (!pairs()) {
            setLimitsLoading(true);
            return;
        }

        if (!pair().isRoutable) {
            setLimitsLoading(false);
            setMinimum(0);
            setMaximum(0);
            return;
        }

        setBoltzFee(pair().feePercentage);

        const swapToCreate = pair().swapToCreate;
        if (!swapToCreate) {
            setLimitsLoading(false);
            return;
        }

        switch (swapToCreate.type) {
            case SwapType.Submarine:
                updateMinerFee(pair().minerFees);
                break;

            case SwapType.Reverse:
            case SwapType.Chain: {
                let fee = pair().minerFees;
                if (
                    isToUnconfidentialLiquid({
                        assetReceive,
                        addressValid,
                        onchainAddress,
                    })
                ) {
                    fee += unconfidentialExtra;
                }

                updateMinerFee(fee);
                break;
            }
        }

        const initiatingPair = pair();
        setLimitsLoading(true);
        void Promise.all([
            initiatingPair.getMinimum(),
            initiatingPair.getMaximum(),
        ])
            .then(([min, max]) => {
                if (pair() !== initiatingPair) {
                    return;
                }

                setMinimum(min);
                setMaximum(max);
                setLimitsLoading(false);
            })
            .catch(() => {
                if (pair() !== initiatingPair) {
                    return;
                }
                setLimitsLoading(false);
            });
    });

    void fetchPairs();

    onMount(() => {
        if (config.isPro) {
            void fetchRegularPairs();
        }
    });

    return (
        <div class="fees-dyn">
            <div class="fees-dyn-denom">
                <Denomination />
            </div>
            <div class="fees-dyn-right">
                <FeesCollapse />
            </div>
        </div>
    );
};

export default Fees;
